import { useCallback, useEffect, useState } from "react";
import { adminUsers, adminDeleteUser, adminUpdateUserPremium, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash, Crown } from "@phosphor-icons/react";
import AdminCard from "../components/AdminCard";
import AdminBadge from "../components/AdminBadge";

export default function UsersTab({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [premiumBusy, setPremiumBusy] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    let mounted = true;
    adminUsers()
      .then((u) => { if (mounted) setUsers(u); })
      .catch((e) => { if (mounted) setErr(formatApiError(e)); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load]);

  const remove = async (user_id) => {
    if (!window.confirm("Delete this user permanently?")) return;
    try {
      await adminDeleteUser(user_id);
      toast.success("User deleted");
      setUsers((u) => u.filter((x) => x.user_id !== user_id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const togglePremium = async (user_id, currentPremium) => {
    const action = currentPremium ? "revoke" : "grant";
    if (!window.confirm(`${action === "grant" ? "Grant" : "Revoke"} premium access for this user?`)) return;
    setPremiumBusy((b) => ({ ...b, [user_id]: true }));
    try {
      const updated = await adminUpdateUserPremium(user_id, !currentPremium);
      setUsers((u) => u.map((x) => x.user_id === user_id ? { ...x, is_premium: updated.is_premium, premium_until: updated.premium_until } : x));
      toast.success(`Premium access ${action === "grant" ? "granted" : "revoked"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPremiumBusy((b) => ({ ...b, [user_id]: false }));
    }
  };

  if (loading) return <p className="text-slate-400 text-sm">Loading…</p>;
  if (err) return <p className="text-red-600 text-sm">{err}</p>;

  return (
    <AdminCard data-testid="users-tab">
      <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between" data-testid="users-tab">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {users.length} registered users
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50">
              {["Name", "Email", "Role", "Premium", "Country", "Joined", ""].map((h) => (
                <th key={h} className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`user-row-${u.user_id}`}>
                <td className="px-6 py-4 font-semibold text-slate-900">{u.name || "—"}</td>
                <td className="px-6 py-4 text-slate-600">{u.email}</td>
                <td className="px-6 py-4">
                  <AdminBadge variant={u.role === "admin" ? "admin" : "free"}>
                    {u.role}
                  </AdminBadge>
                </td>
                <td className="px-6 py-4">
                  {u.role !== "admin" ? (
                    <button
                      onClick={() => togglePremium(u.user_id, u.is_premium)}
                      disabled={!!premiumBusy[u.user_id]}
                      className="disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`premium-toggle-${u.user_id}`}
                      aria-label={u.is_premium ? "Revoke premium" : "Grant premium"}
                    >
                      <AdminBadge variant={u.is_premium ? "premium" : "free"}>
                        <Crown size={10} weight={u.is_premium ? "fill" : "regular"} />
                        {u.is_premium ? "Premium" : "Free"}
                      </AdminBadge>
                    </button>
                  ) : (
                    <span className="text-slate-300 text-xs">—</span>
                  )}
                </td>
                <td className="px-6 py-4 text-slate-500 text-xs">{u.country || "—"}</td>
                <td className="px-6 py-4 text-slate-400 text-xs">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-6 py-4 text-right">
                  {u.role !== "admin" && u.user_id !== currentUserId && (
                    <button
                      onClick={() => remove(u.user_id)}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      data-testid={`delete-user-${u.user_id}`}
                      aria-label="Delete user"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminCard>
  );
}
